import { computed, Injectable, Signal } from '@angular/core';
import { HttpQueryInterface, HttpRequestType, HttpService } from '@shared/http/http.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CourseContentService, ShortCodes, ShortCodeValue } from '@app/courseContent/courseContent.service';
import { ParticipantService } from '@shared/services/participant/participant.service';
import './vfs_fonts';
import {
  Content,
  ContentImage,
  ContentOrderedList,
  ContentTable,
  ContentText,
  ContextPageSize,
  DynamicBackground,
  DynamicContent,
  Size,
  Table,
  TDocumentDefinitions,
  TDocumentInformation,
  TFontDictionary,
} from 'pdfmake/interfaces';

/**
 * STRANGE BUG: if we import from pdfmake/interfaces, we get this error
 *    ERROR in ./src/app/@shared/services/pdfGenerator/pdfGenerator.service.ts 4:0-49
 *    Module not found: Error: Can't resolve 'pdfmake/interfaces' in 'src/app/@shared/services/pdfGenerator'
 * It doesn't make sense to me but importing it from a copy of pdfmake/interfaces solves the issue
 */
import { ContentStack } from './pdfmake-types-copy';
import * as pdfMake from 'pdfmake/build/pdfmake.js';
import { ParticipantModel } from '@shared/models/participant.model';

const fonts: TFontDictionary = {
  Rubik: {
    normal: 'Rubik-Regular.ttf',
    bold: 'Rubik-Bold.ttf',
    italics: 'Rubik-Regular.ttf',
    bolditalics: 'Rubik-Bold.ttf',
  },
};

export interface PlTable extends Omit<Table, 'headerRows' | 'keepWithHeaderRows' | 'widths'> {
  headerRows: boolean;
  keepWithHeaderRows: boolean;
  widths: string | '*' | 'auto' | Size[] | undefined;
}

export interface PlContentTable extends Omit<ContentTable, 'table'> {
  table: PlTable;
}

export interface PlContentRepeatable extends ContentStack {
  repeat: {
    start: number;
    end: number;
  };
  pageBreakAfter?: number;
}

export function isContentRepeatable(value: Content | PlContent): value is PlContentRepeatable {
  return value.constructor === Object && value.hasOwnProperty('stack') && value.hasOwnProperty('repeat');
}

export type PlContent =
  | PlContent[]
  | PlContentTable
  | PlContentRepeatable
  | ContentText
  | ContentOrderedList
  | ContentStack
  | ContentImage;

export interface PlTDocumentDefinitions extends Omit<TDocumentDefinitions, 'content' | 'footer'> {
  content: PlContent;
  backgroundImages?: (string | null)[];
  footer?: DynamicContent | Content | undefined | {};
}

export interface PdfConfigFileOnAws {
  data: PlTDocumentDefinitions;
}

export interface PdfConfigVariables {
  plOrange: string;
  firstName: string;
  lastName: string;
  currentYear: number;
}

export interface GlobalPlConfigValues extends PdfConfigVariables {
  tableNoteWidth: [number, number, number];
  tablePlaybookWidth: [number, number];
  cellHeaderBorder: [boolean, boolean, boolean, boolean];
  cellContentTopBorder: [boolean, boolean, boolean, boolean];
}

@Injectable({
  providedIn: 'root',
})
export class PdfGeneratorService {
  private reShortcode: RegExp = /{!!\s?([a-zA-Z0-9.\-_]+)\s?!!}/g;

  private globalPlConfigValues: Signal<GlobalPlConfigValues> = computed(() =>
    this.getGlobalConfigValues(this.participantService.participant()),
  );

  private readonly MAX_LENGTH_SHORT_CODE_VALUE: number = 500;

  constructor(
    private httpService: HttpService,
    private participantService: ParticipantService,
    private courseContentService: CourseContentService,
  ) {}

  public generateAndDownloadPdf(templateUrl: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const requestOptions: HttpQueryInterface = {
        type: HttpRequestType.GET,
        url: templateUrl,
      };

      this.httpService.request(requestOptions).subscribe({
        next: async (configFileOnAws: PdfConfigFileOnAws) => {
          const contentWithoutRepeat: PlContent = this.flattenRepeatStacks(configFileOnAws.data.content);

          const pdfMakeConfigFile: PlTDocumentDefinitions = {
            ...configFileOnAws.data,
            content: contentWithoutRepeat,
          };

          const pdfMakeConfigFileAsString: string = JSON.stringify(pdfMakeConfigFile);
          const shortCodesInTemplate: ShortCodes = await this.getShortCodesFromConfig(pdfMakeConfigFileAsString);

          const shortCodeWithSpecialCharactersReplaced: ShortCodes =
            this.replaceSpecialCharacters(shortCodesInTemplate);

          const pdfMakeConfigAsStringWithShortCodesValueReplaced: string = this.replaceShortCodeValue(
            pdfMakeConfigFileAsString,
            shortCodeWithSpecialCharactersReplaced,
          );

          const pdfMakeConfigAsStringWithGlobalConfigReplaced: string = this.replaceGlobalConfigValue(
            pdfMakeConfigAsStringWithShortCodesValueReplaced,
          );

          const pdfMakeConfigAsStringWithVariablesReplaced: string = this.replaceVariables(
            pdfMakeConfigAsStringWithGlobalConfigReplaced,
          );

          const pdfMakeConfigWithAllStyles: TDocumentDefinitions = this.addStylesIfNotDefined(
            JSON.parse(pdfMakeConfigAsStringWithVariablesReplaced),
          );

          const pdfTitle: string = this.createPdfTitle(pdfMakeConfigWithAllStyles.info);

          pdfMake.createPdf(pdfMakeConfigWithAllStyles, undefined, fonts).download(pdfTitle);
          resolve();
        },
        error: (err: HttpErrorResponse) => {
          reject(err);
        },
      });
    });
  }

  public flattenRepeatStacks(content: PlContent): PlContent {
    if (Array.isArray(content)) {
      const contentFlattened: PlContent = [];

      content.forEach((block: PlContent) => {
        if (isContentRepeatable(block)) {
          const contentStack: ContentStack = {
            stack: [],
          };
          const stackToRepeatAsString: string = JSON.stringify(block.stack[0]);

          const reReplaceIndex: RegExp = new RegExp('%index%', 'g');

          for (let index: number = block.repeat.start; index <= block.repeat.end; index++) {
            const stackToRepeatWithIndex: string = stackToRepeatAsString.replace(reReplaceIndex, (): string => {
              return `${index}`;
            });

            const newBlock: ContentText | ContentStack = JSON.parse(stackToRepeatWithIndex);

            if (block.pageBreakAfter === index) {
              newBlock.pageBreak = 'after';
            }

            contentStack.stack.push(newBlock);
          }

          contentFlattened.push(contentStack);
        } else {
          contentFlattened.push(block);
        }
      });

      return contentFlattened;
    } else {
      return content;
    }
  }

  public async getShortCodesFromConfig(pdfMakeConfigFileAsString: string): Promise<ShortCodes> {
    const allShortCodesFromConfig: string[] = this.extractShortCodeFromConfig(pdfMakeConfigFileAsString);
    const shortCodesPopulatedFromCache: ShortCodes = this.retrieveShortCodesFromCache(allShortCodesFromConfig);

    const shortCodesFromCacheAndServer: ShortCodes =
      await this.retrieveRemainingShortCodesFromServer(shortCodesPopulatedFromCache);

    const allShortCodesWithLongValueShorten: ShortCodes =
      this.shortenShortCodesLongValueIfAny(shortCodesFromCacheAndServer);

    return Promise.resolve(allShortCodesWithLongValueShorten);
  }

  public extractShortCodeFromConfig(pdfMakeConfigFileAsString: string): string[] {
    const shortCodeList: string[] = [];

    let match: RegExpExecArray | null = this.reShortcode.exec(pdfMakeConfigFileAsString);
    while (match !== null) {
      shortCodeList.push(match[1]);
      match = this.reShortcode.exec(pdfMakeConfigFileAsString);
    }

    return shortCodeList;
  }

  public retrieveShortCodesFromCache(shortCodesList: string[]): ShortCodes {
    const shortCodes: ShortCodes = {};

    shortCodesList.forEach((shorCode: string) => {
      shortCodes[shorCode] = this.courseContentService.getShortCodeSavedInLocalStorage(shorCode);
    });

    return shortCodes;
  }

  public async retrieveRemainingShortCodesFromServer(shortCodes: ShortCodes): Promise<ShortCodes> {
    const shortCodesToRetrieve: string[] = Object.entries(shortCodes)
      .filter(([, shortCodeValue]: [string, ShortCodeValue]): boolean => {
        return shortCodeValue === null;
      })
      .map(([shortCodeKey]: [string, ShortCodeValue]): string => {
        return shortCodeKey;
      });

    if (shortCodesToRetrieve.length === 0) {
      return shortCodes;
    } else {
      const shortCodesFromServer: ShortCodes = await this.courseContentService.getShortcodes(shortCodesToRetrieve);
      return {
        ...shortCodes,
        ...shortCodesFromServer,
      };
    }
  }

  public replaceSpecialCharacters(shortCodes: ShortCodes): ShortCodes {
    const shortCodesReplaced: ShortCodes = {};

    Object.entries(shortCodes).forEach(([shortCodeKey, shortCodeValue]: [string, ShortCodeValue]): void => {
      if (typeof shortCodeValue === 'string') {
        const updatedValue: string = shortCodeValue.replace(/((\\.)|(["']))|([\n\r\t\b])/g, (match) => {
          let replaceString: string = '';
          if (match.includes("'")) {
            replaceString = "'";
          } else if (match.includes('"')) {
            replaceString = '\\"';
          } else if (match.includes('\n') || match.includes('\r')) {
            replaceString = '\\n';
          } else if (match.includes('\t')) {
            replaceString = ' ';
          } else if (match.includes('\b')) {
            replaceString = '';
          } else if (match.includes('\\')) {
            // Problem when shortcode contains \\\\, it mess up everything, I think what the user entered at the beginning
            // was \\ and he will be returned \
            replaceString = '\\\\';
          } else {
            replaceString = `\\${match}`;
          }

          return replaceString;
        });

        shortCodesReplaced[shortCodeKey] = updatedValue;
      } else {
        shortCodesReplaced[shortCodeKey] = '';
      }
    });

    return shortCodesReplaced;
  }

  public replaceShortCodeValue(pdfMakeConfigFileAsString: string, shortCodes: ShortCodes): string {
    const templacePdfWithData: string = pdfMakeConfigFileAsString.replace(
      this.reShortcode,
      (match: string, shortcode: string): string => {
        const shortCodeValue: ShortCodeValue = shortCodes[shortcode];
        if (typeof shortCodeValue === 'string' || shortCodeValue === null) {
          return shortCodeValue || '';
        } else {
          console.error(`We should not have something different than string or null here. shortCodes=`, shortCodes);
          throw new Error(`Not normal to have the viaSurvey strength shortcode inside the PDF generator`);
        }
      },
    );

    return templacePdfWithData;
  }

  public replaceGlobalConfigValue(pdfMakeConfigFileAsString: string): string {
    const reConfig: RegExp = /"{!!config\s?([a-zA-Z0-9.\-_]+)\s?!!}"/g;
    const templacePdfWithConfig: string = pdfMakeConfigFileAsString.replace(
      reConfig,
      (match: string, configStr: keyof GlobalPlConfigValues): string => {
        return JSON.stringify(this.globalPlConfigValues()[configStr]);
      },
    );

    return templacePdfWithConfig;
  }

  public replaceVariables(pdfMakeConfigFileAsString: string): string {
    const reVariable: RegExp = /{!!var\s?([a-zA-Z0-9.\-_]+)\s?!!}/g;
    const templacePdfWithVariables: string = pdfMakeConfigFileAsString.replace(
      reVariable,
      (match: string, variableName: keyof PdfConfigVariables): string => {
        return this.globalPlConfigValues()[variableName]
          ? `${this.globalPlConfigValues()[variableName]}`
          : 'VARIABLE NOT FOUND';
      },
    );

    return templacePdfWithVariables;
  }

  private getGlobalConfigValues(participant: ParticipantModel | null): GlobalPlConfigValues {
    return {
      tableNoteWidth: [365, 150, 30],
      tablePlaybookWidth: [278, 277],
      cellHeaderBorder: [true, true, true, false],
      cellContentTopBorder: [true, false, true, true],
      plOrange: '#f0673c',
      firstName: participant?.first_name || '',
      lastName: participant?.last_name || '',
      currentYear: new Date().getFullYear(),
    };
  }

  public addStylesIfNotDefined(pdfMakeConfig: PlTDocumentDefinitions): TDocumentDefinitions {
    const pdfMakeConfigOverridden: TDocumentDefinitions = JSON.parse(JSON.stringify(pdfMakeConfig));

    // if config is defined in the pDF, overwrite default value in config
    if (!pdfMakeConfig.footer) {
      pdfMakeConfigOverridden.footer = {
        stack: [
          {
            image: 'logo',
            height: 40,
            width: 200,
            alignment: 'left',
          },
        ],
        margin: [20, -5, 0, 0],
      };
    }

    if (!pdfMakeConfig.pageMargins) {
      pdfMakeConfigOverridden.pageMargins = [10, 10, 10, 40];
    }

    if (!pdfMakeConfig.defaultStyle) {
      pdfMakeConfigOverridden.defaultStyle = {
        font: 'Rubik',
      };
    }

    if (pdfMakeConfig.backgroundImages) {
      pdfMakeConfigOverridden.background = this.generateBackgroundFunction(pdfMakeConfig.backgroundImages);
    }

    // Concatenate additional styles and images to the existing ones
    pdfMakeConfigOverridden.styles = {
      headerOrange: {
        fontSize: 12,
        bold: true,
        color: '#ec754e',
        margin: [0, 0, 0, 8],
      },
      h2: {
        fontSize: 16,
        lineHeight: 1.2,
        bold: true,
        margin: [0, 0, 0, 4],
      },
      tableNoteHeader: {
        fillColor: '#E9E9E9',
        fontSize: 12,
        bold: true,
        margin: [3, 5, 0, 3],
      },
      tableNote: {
        margin: [0, 5, 0, 5],
      },
      viaStrengthHeaders: {
        fontSize: 16,
        lineHeight: 1.2,
        bold: true,
        margin: [0, 0, 0, 0],
      },
      viaStrength: {
        margin: [0, 6, 0, 6],
        alignment: 'justify',
      },
      tickColumn: {
        margin: [0, 5, 0, 5],
      },
      textTableNote: {
        fontSize: 10,
        margin: [0, 2, 0, 2],
      },
      textOrderedList: {
        margin: [0, 2, 0, 2],
      },
      bold: {
        bold: true,
      },
      shortCode: {
        color: '#0093C0',
        bold: true,
      },
      ...pdfMakeConfigOverridden.styles,
    };

    pdfMakeConfigOverridden.images = {
      tick: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAZCAYAAABQDyyRAAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAZEQAAGREBkIelaAAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAALLSURBVEiJzZZLSFRhFMd/586MaYZU2IMkWkRRRAuhx8ae0sYIydFxxiDUhZg6VLSoTeC2CFJmsuhB9oAc59ET7GW1ija1CsuoXRFC0ANDR733tMjMsZm5amN0luf8v/P7853vft8V/kHUd1YUOB2ONqAYeC1oU8AdegYgMw1vjFYtE6xuYPm4dBzwBN0dt2bUQGOscqWoPASWJikPGqLrHTMGj/jWCDwGClJInBYMGDMB90e8hSL6GFicTifK54yPoDHi3ShCFzDPRvrWaWSvz+gONMR8RQL37eAq0jtimltbdrd/yZgBf8y3xVDtQshLKxTpcalj2xlP+ANAgoED16vnHrhePXfK8Kh3p6reBebYSJ8rrs0t7qsfx/wAVF+szp6TF28DrRnNXur/ml3fXtM+aAuPVZapyjUgK51O4emwaZac9YS/js9L3e1ds7OGcm8AOxIKQvesflfpib1Xvqdq2hT1VgGXAGdal8ITa8Tc1eYJ908sGa7h3MBEOIAqxfHc4bv+q3uSztQf8dYCl23h6D3XiFmSDA5giEV5yqVQpDnmg313qhJOdVPU16DCecDuIrslOd9KT3rCA6kEBsI7myYbHIPmI39nxQIAf9R3CDSIzTsiQufQp7zyQElXPK1u9OJ4iN0JFukR1dsKh20MA3K5zxypDXvCpq0SoCniXYdwD5hv39wmlHP5L1fVNzc3W5ORj22jP+ItRLivkD9dtsDpQFlHI4JOYc3vaIhVrjZ+Pp9Lpo7X40F3aBLjSYyEm7CtLPTKYZrbgfdTaSJwbDrwPwwAtHrCvU6DTWD7dfyCHw24O45MBz66Pnk03PAuNUy6gRUpJIrqwWB5qHW68LQGAPbf9C0yh60HiKydULKAuqC748LfwCHJCMZHa+m1PrGsYuDFuHRcRPdmAg6T/Ctu7qzI+mQ4PYq1EIM7p8pCbzIB/y/iBws47PapnBs3AAAAAElFTkSuQmCC',
      logo: 'data:image/png;base64,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',
      circleWithSpaceTopBottom:
        'data:image/png;base64,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',
      ...pdfMakeConfigOverridden.images,
    };

    return pdfMakeConfigOverridden;
  }

  private generateBackgroundFunction(backgroundImages: (string | null)[]): DynamicBackground | Content | undefined {
    return (currentPageNumber: number, pageSize: ContextPageSize): Content | null | undefined => {
      let backgroundImage: [ContentImage] | null = null;
      const indexBackGroundIMageMatchingPageNumber: number = currentPageNumber - 1;
      if (indexBackGroundIMageMatchingPageNumber < backgroundImages.length) {
        const currentPageBackgroundImage: string | null = backgroundImages[indexBackGroundIMageMatchingPageNumber];
        if (currentPageBackgroundImage !== null) {
          backgroundImage = [
            {
              image: currentPageBackgroundImage,
              width: pageSize.width,
              height: pageSize.height,
            },
          ];
        }
      }

      return backgroundImage;
    };
  }

  public createPdfTitle(info: TDocumentInformation | undefined): string {
    const pdfExtension: string = '.pdf';

    let title: string = info && info.title ? info.title : 'Potentialife.pdf';

    if (title.slice(-pdfExtension.length) !== pdfExtension) {
      title += pdfExtension;
    }

    return title;
  }

  private shortenShortCodesLongValueIfAny(shortCodes: ShortCodes): ShortCodes {
    const shortCodesShortened: ShortCodes = {};
    Object.entries(shortCodes).forEach(([shortCodeKey, shortCodeValue]: [string, ShortCodeValue]): void => {
      if (typeof shortCodeValue === 'string' && shortCodeValue.length > this.MAX_LENGTH_SHORT_CODE_VALUE) {
        shortCodesShortened[shortCodeKey] = `${shortCodeValue.slice(0, this.MAX_LENGTH_SHORT_CODE_VALUE)}...`;
      } else {
        shortCodesShortened[shortCodeKey] = shortCodeValue;
      }
    });

    return shortCodesShortened;
  }
}
